import { formatMultipleSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import Page from '@app/components/layout/page/page';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import useSimpleDialog from '@app/hooks/use-simple-dialog';
import { useGetStationaryCombustionActivityList } from '@shared/api/hooks/ccf/activities/stationary-combustion';
import Text from '@shared/components/content/text';
import TextList from '@shared/components/content/text-list';
import { useCCF } from '../../ccf-context';
import StationaryCombustionEditForm from '../../components/scope-1/stationary-combustion-edit-form';
import StationaryCombustionTable from '../../components/scope-1/stationary-combustion-table';
import { reportSlugs } from '../../slugs';
import { useCategory } from '../../utils';
import EmptyCategoryPage from '../ccf-category-intro-page';

export default function StationaryCombustionPage() {
  const { uuid: reportUuid } = useCCF();
  const category = useCategory(reportSlugs.stationaryCombustion);
  const {
    showSimpleDialog: showStationaryCombustionDialog,
    SimpleDialogComponent: StationaryCombustionDialog,
    closeSimpleDialog: closeStationaryCombustionDialog,
  } = useSimpleDialog();

  const { page, perPage, period, site, sort, supplier, fuel } =
    useDataTableQueryState({
      filters: ['supplier', 'period', 'site', 'fuel'],
    });

  const {
    data,
    isPreviousData,
    isFetchedAfterMount,
    isLoading,
    isFetching,
    status,
  } = useGetStationaryCombustionActivityList(reportUuid!, {
    page,
    page_size: perPage,
    suppliers: supplier || undefined,
    time_periods: period || undefined,
    sites: site || undefined,
    fuels: fuel || undefined,
    sort: formatMultipleSortingForEndpoint(sort! || []),
  });

  const description = (
    <>
      <Text>
        Stationary combustion refers to the{' '}
        <span className="font-semibold">mains gas supply</span> for your sites,
        as well as any{' '}
        <span className="font-semibold">additional fuels you burn on site</span>
        , such as natural gas, diesel, or propane. These activities produce
        direct emissions and fall under Scope 1 of your company carbon
        footprint.
      </Text>
      <Text className="mt-3">Examples include:</Text>
      <TextList>
        <li>
          <span className="font-semibold">Heating buildings</span>
        </li>
        <li>
          <span className="font-semibold">Running boilers</span>
        </li>
        <li>
          <span className="font-semibold">Powering generators</span>
        </li>
      </TextList>
    </>
  );

  return (
    <Page name="Scope 1 - Stationary combustion" page="subpage">
      {category.hasActivityData ? (
        <StationaryCombustionTable
          data={data!}
          status={status}
          isLoading={isLoading}
          isFetching={isFetching}
          isPreviousData={isPreviousData}
          isFetchedAfterMount={isFetchedAfterMount}
        />
      ) : (
        <EmptyCategoryPage
          categorySlug="stationary-combustion"
          description={description}
          primaryActionProps={{
            onClick: showStationaryCombustionDialog,
          }}
        />
      )}
      {StationaryCombustionDialog({
        header: 'Add stationary combustion data',
        content: (
          <StationaryCombustionEditForm
            closeDialog={closeStationaryCombustionDialog}
          />
        ),
      })}
    </Page>
  );
}
